import { useEffect, useState } from "react";
import PendingLogin from "./PendingLogin";
import ApplicantLoggedIn from "./ApplicantLoggedIn";
import ModifyInviteModal from "../../../core/common/ModifyInviteModal";
import TimelineModal from "../../../core/common/TimelineModal";
import { Button } from "antd";
import GuideContentArea from "../../../core/common/GuideContentArea";
import { timelineData_invite } from "../../../core/data/js/dummy-data";
import Footer from "../../../core/common/Footer";
import TotalInviteSent from "./TotalInviteSent";
import ApplicantInProgress from "./ApplicantInProgress";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import { PageLoader } from "../../../core/common/loader/PageLoader";
import WorkInProgress from "./WorkInProgress";
import {
  FileTextOutlined,
  ClockCircleOutlined,
  LoginOutlined,
  SyncOutlined,
  FundProjectionScreenOutlined,
} from "@ant-design/icons";

interface CountData {
  status_id: string;
  label: string;
  count: number;
}

export const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  pending: { bg: "#FFF4E5", color: "#B76E00" },
  "in progress": { bg: "#E8F0FE", color: "#1A56DB" },
  accepted: { bg: "#E6F7ED", color: "#0F9D58" },
  completed: { bg: "#E6F7ED", color: "#0F9D58" },
  expired: { bg: "#FDECEB", color: "#D93025" },
  rejected: { bg: "#FDECEB", color: "#D93025" },
  active: { bg: "#E6F7ED", color: "#0F9D58" },
  "in active": { bg: "#FDECEB", color: "#D93025" },
};

export const DEFAULT_STATUS_STYLE = { bg: "#F1F2F4", color: "#5F6570" };

const ACTIVE_TAB_KEY = "managerCaseTrackActiveTab";

const WIP_COUNT_URL = "/api/v1/agency/manager-live-case-progress-count";

const guideData = [
  {
    title: "Stage 1 - Invites Sent",
    steps: [
      "All invites sent will by default show a pending login status. The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
    ],
  },
  {
    title: "Stage 2 - Pending Log In",
    steps: [
      "The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
    ],
  },
  {
    title: "Stage 3 - Logged In",
    steps: [
      "The invite has been opened by the candidate, and they have proceeded to fill the form. Form filling is in progress.",
    ],
  },
  {
    title: "Stage 4 - Invites Stopped/Expired",
    steps: [
      "All invites which were not filled by the candidate will expire after a certain period of time.",
      "The default time period is set at 15 working days. If you would like to change this time period, you can do so in the master settings tab (sub tab).",
      "All invites stopped will be added to this bucket and can be resumed at a later date.",
    ],
  },
  {
    title: "Stage 5 - Form Submitted | Awaiting HR Approval",
    steps: [
      "The form has been successfully filled by the applicant and it is now submitted.",
      "The case will either go directly to Aupertz for starting of the background check or it will go to the client's HR for a pre-approval before it gets sent to Aupertz.",
      "By default it will go to Aupertz directly.",
      "If you would like HR approval, this can be triggered in the master settings tab (sub tab).",
    ],
  },
];

const ApplicantCaseTracking = () => {
  const [data, setData] = useState<CountData[]>([]);
  const [wipData, setWipData] = useState<CountData[]>([]);

  const getTotalCounts = useApi<any>(
    webservice_url.manager_case_progress_count,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getWipCounts = useApi<any>(WIP_COUNT_URL, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const fetchData = async () => {
    // Dono API parallel chalengi, ek fail hui to dusri ka data phir bhi aayega
    const [totalRes, wipRes] = await Promise.allSettled([
      getTotalCounts.callApi({}),
      getWipCounts.callApi({}),
    ]);

    if (totalRes.status === "fulfilled") {
      setData(totalRes.value?.data ?? []);
    } else {
      console.error(totalRes.reason);
      setData([]);
    }

    if (wipRes.status === "fulfilled") {
      setWipData(wipRes.value?.data ?? []);
    } else {
      console.error(wipRes.reason);
      setWipData([]);
    }
  };

  const getCount = (label: string) =>
    data.find((item) => item?.label === label)?.count ?? 0;

  const getStatusId = (label: string) =>
    data.find((item) => item?.label === label)?.status_id ?? "";

  const totalInvitationsCount =
    data?.find((item) => item?.label === "Total")?.count ?? 0;

  // Work In Progress ke liye alag helpers (wipData se)
  const wipItem = wipData.find((item) => item?.label === "Work In Progress");
  const wipCount = wipItem?.count ?? 0;
  const wipStatusId = wipItem?.status_id ?? "";

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("tab")) {
      url.searchParams.delete("tab");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

   const ApplicantStats = [
    {
      id: "total_invite_sent",
      label: "Total Invitations",
      icon: <FileTextOutlined />,
      count: totalInvitationsCount,
      component: <TotalInviteSent status_id={getStatusId("Total")} />,
    },
    {
      id: "pending_login",
      label: "Pending Login",
      icon: <ClockCircleOutlined />,
      count: getCount("Login Pending"),
      component: <PendingLogin status_id={getStatusId("Login Pending")} />,
    },
    {
      id: "logged_in",
      label: "Logged In",
      icon: <LoginOutlined />,
      count: getCount("Logged In"),
      component: <ApplicantLoggedIn status_id={getStatusId("Logged In")} />,
    },
    {
      id: "in_progress",
      label: "In Progress",
      icon: <SyncOutlined />,
      count: getCount("In Progress"),
      component: <ApplicantInProgress status_id={getStatusId("In Progress")} />,
    },
    {
      id: "workInProgress",
      label: "Live Case Progress",
      icon: <FundProjectionScreenOutlined />,
      count: wipCount,
      component: <WorkInProgress status_id={wipStatusId} />,
    },
  ];
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);
  const [showGuide, setShowGuide] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      return localStorage.getItem(ACTIVE_TAB_KEY) || "total_invite_sent";
    } catch {
      return "total_invite_sent";
    }
  });

  const activeItem = ApplicantStats.find((i) => i.id === activeTab);
  const activeComponent = activeItem ? activeItem.component : <></>;

  useEffect(() => {
    const valid = ApplicantStats.some((s) => s.id === activeTab);
    if (!valid && ApplicantStats[0]?.id) {
      setActiveTab(ApplicantStats[0].id);
    }
  }, [data, wipData]);

  // handle tab click
  const handleTabClick = (d: { id: string }) => {
    setActiveTab(d.id);
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, d.id);
    } catch (err) {
      console.error("Failed to save active tab to localStorage:", err);
    }
  };

  const isLoading = getTotalCounts.loading || getWipCounts.loading;
  const CaseMark = () => (
    <svg
      className="tcp-case-mark"
      viewBox="0 0 40 32"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="14" width="4" height="18" rx="1.5" />
      <rect x="9" y="8" width="4" height="24" rx="1.5" />
      <rect x="18" y="0" width="4" height="32" rx="1.5" />
      <rect x="27" y="10" width="4" height="22" rx="1.5" />
      <rect x="36" y="18" width="4" height="14" rx="1.5" />
    </svg>
  );
  return (
    <div className="page-wrapper">
      <div className="content">
        {isLoading ? (
          <PageLoader />
        ) : (
          <>
            <div className="tcp-header-title-row d-flex gap-3 mb-3">
              <CaseMark />
              <div>
                <h4 className="tcp-title">Case Tracking</h4>
                <p className="tcp-subtitle">
                  Track candidate cases, monitor screening progress, and manage
                  invitations from one workspace.
                </p>
              </div>
            </div>
            {/* Tabs */}
            {/* <TabItems
              tabList={ApplicantStats}
              activeTab={activeTab}
              handleClick={handleTabClick}
            /> */}
            {/* <ul className="invite-tabs">
              {ApplicantStats.map((tab) => (
                <li key={tab.id}>
                  <button
                    type="button"
                    className={`invite-btn ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => handleTabClick(tab)}
                  >
                    <span className="d-block">{tab.label}</span>
                    {typeof tab.count === "number" && (
                      <span className="d-block fw-semibold">{tab.count}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul> */}

            <ul className="invite-tabs">
              {ApplicantStats.map((tab) => (
                <li key={tab.id}>
                  <button
                    type="button"
                    className={`invite-btn ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => handleTabClick(tab)}
                  >
                    <span className="d-flex align-items-center justify-content-center gap-2">
                      {tab.icon}
                      <span>{tab.label}</span>
                    </span>

                    {typeof tab.count === "number" && (
                      <span className="d-block fw-semibold">{tab.count}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>

            {/* Guide Container */}
            <>
              <div className="text-end">
                <Button
                  className="border-secodary"
                  onClick={() => setShowGuide((prev) => !prev)}
                >
                  <i className="ti ti-info-circle-filled fs-16"></i>
                  <div className="pb-1 ">Guide</div>
                </Button>
              </div>

              <GuideContentArea
                maxHeight={"400px"}
                showGuide={showGuide}
                handleClick={() => setShowGuide(false)}
                title={"Applicant Invited"}
                content={
                  <div>
                    <div className="pb-2">
                      The applicant will receive the user credentials to access
                      the Aupertz applicant portal through an invite.
                    </div>
                    <div className="pb-4">
                      The applicant will log in to fill the applicant form (ADF)
                      and submit all the required data for screening.
                    </div>
                    {guideData?.map((d) => (
                      <div
                        key={d.title}
                        className="mb-3"
                        style={{ maxWidth: "900px" }}
                      >
                        <h6 className="fw-semibold pb-1">{d.title}</h6>
                        {d?.steps?.map((i, idx) => (
                          <div key={idx} className="d-flex gap-2 py-1">
                            <div>{idx + 1}.</div>
                            <div>{i}</div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                }
              />
            </>

            {/* Tab Content */}
            <div className="mt-2">{activeComponent}</div>

            {/* Timeline Modal */}
            {showTimeline && (
              <TimelineModal
                timelineData={timelineData_invite}
                onClose={() => setShowTimeline(false)}
              />
            )}
            {showModifyInvite && (
              <ModifyInviteModal
                handleClose={() => setShowModifyInvite(false)}
                handleSubmit={(d) => {
                  console.log(d);
                  setShowModifyInvite(false);
                }}
              />
            )}
          </>
        )}
      </div>

      {/* ---- Footer ---- */}
      <Footer />
    </div>
  );
};
export default ApplicantCaseTracking;

// import { useEffect, useState } from "react";
// import PendingLogin from "./PendingLogin";
// import ApplicantLoggedIn from "./ApplicantLoggedIn";
// import ModifyInviteModal from "../../../core/common/ModifyInviteModal";
// import TimelineModal from "../../../core/common/TimelineModal";
// import { Button } from "antd";
// import GuideContentArea from "../../../core/common/GuideContentArea";
// import { timelineData_invite } from "../../../core/data/js/dummy-data";
// import TabItems from "../../../core/common/TabItems";
// import Footer from "../../../core/common/Footer";
// import TotalInviteSent from "./TotalInviteSent";
// import ApplicantInProgress from "./ApplicantInProgress";
// import useApi from "../../../core/customhook/useApi";
// import webservice_url from "../../../core/webservice/Webservice_url";
// import { PageLoader } from "../../../core/common/loader/PageLoader";
// import WorkInProgress from "./WorkInProgress";

// interface CountData {
//   status_id: string;
//   label: string;
//   count: number;
// }

// export const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
//   pending: { bg: "#FFF4E5", color: "#B76E00" },
//   "in progress": { bg: "#E8F0FE", color: "#1A56DB" },
//   accepted: { bg: "#E6F7ED", color: "#0F9D58" },
//   completed: { bg: "#E6F7ED", color: "#0F9D58" },
//   expired: { bg: "#FDECEB", color: "#D93025" },
//   rejected: { bg: "#FDECEB", color: "#D93025" },
//   active: { bg: "#E6F7ED", color: "#0F9D58" },
//   "in active": { bg: "#FDECEB", color: "#D93025" },
// };

// export const DEFAULT_STATUS_STYLE = { bg: "#F1F2F4", color: "#5F6570" };

// const ACTIVE_TAB_KEY = "managerCaseTrackActiveTab";

// const guideData = [
//   {
//     title: "Stage 1 - Invites Sent",
//     steps: [
//       "All invites sent will by default show a pending login status. The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
//     ],
//   },
//   {
//     title: "Stage 2 - Pending Log In",
//     steps: [
//       "The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
//     ],
//   },
//   {
//     title: "Stage 3 - Logged In",
//     steps: [
//       "The invite has been opened by the candidate, and they have proceeded to fill the form. Form filling is in progress.",
//     ],
//   },
//   {
//     title: "Stage 4 - Invites Stopped/Expired",
//     steps: [
//       "All invites which were not filled by the candidate will expire after a certain period of time.",
//       "The default time period is set at 15 working days. If you would like to change this time period, you can do so in the master settings tab (sub tab).",
//       "All invites stopped will be added to this bucket and can be resumed at a later date.",
//     ],
//   },
//   {
//     title: "Stage 5 - Form Submitted | Awaiting HR Approval",
//     steps: [
//       "The form has been successfully filled by the applicant and it is now submitted.",
//       "The case will either go directly to Aupertz for starting of the background check or it will go to the client's HR for a pre-approval before it gets sent to Aupertz.",
//       "By default it will go to Aupertz directly.",
//       "If you would like HR approval, this can be triggered in the master settings tab (sub tab).",
//     ],
//   },
// ];

// const ApplicantCaseTracking = () => {
//   const [data, setData] = useState<CountData[]>([]);

//   const getTotalCounts = useApi<any>(
//     webservice_url.manager_case_progress_count,
//     {
//       method: "GET",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const fetchData = async () => {
//     try {
//       const res = await getTotalCounts.callApi({});
//       setData(res?.data ?? []);
//     } catch (err) {
//       console.error(err);
//       setData([]);
//     }
//   };

//   const getCount = (label: string) =>
//     data.find((item) => item?.label === label)?.count ?? 0;

//   const getStatusId = (label: string) =>
//     data.find((item) => item?.label === label)?.status_id ?? "";

//   const totalInvitationsCount =
//     data?.find((item) => item?.label === "Total")?.count ?? 0;

//   useEffect(() => {
//     fetchData();
//   }, []);

//   useEffect(() => {
//     const url = new URL(window.location.href);
//     if (url.searchParams.has("tab")) {
//       url.searchParams.delete("tab");
//       window.history.replaceState({}, "", url.toString());
//     }
//   }, []);

//   const ApplicantStats = [
//     {
//       id: "total_invite_sent",
//       label: "Total Invitations",
//       count: totalInvitationsCount,
//       component: <TotalInviteSent status_id={getStatusId("Total")} />,
//     },
//     // {
//     //   id: "pre_bgv",
//     //   label: "Pre BGV",
//     //   count: getCount("Pre BGV"),
//     //   component: <PreBgv />,
//     // },
//     {
//       id: "pending_login",
//       label: "Login Pending",
//       count: getCount("Login Pending"),
//       component: <PendingLogin status_id={getStatusId("Login Pending")} />,
//     },
//     {
//       id: "logged_in",
//       label: "Logged In",
//       count: getCount("Logged In"),
//       component: <ApplicantLoggedIn status_id={getStatusId("Logged In")} />,
//     },
//     {
//       id: "in_progress",
//       label: "In Progress",
//       count: getCount("In Progress"),
//       component: <ApplicantInProgress status_id={getStatusId("In Progress")} />,
//     },
//     {
//       id: "workInProgress",
//       label: "Work In Progress",
//       // count: dummyData_WorkInProgress?.length || 0,
//       component: <WorkInProgress status_id={getStatusId("Work In Progress")} />,
//     },
//   ];

//   const [showTimeline, setShowTimeline] = useState<boolean>(false);
//   const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);
//   const [showGuide, setShowGuide] = useState<boolean>(false);

//   const [activeTab, setActiveTab] = useState<string>(() => {
//     try {
//       return localStorage.getItem(ACTIVE_TAB_KEY) || "total_invite_sent";
//     } catch {
//       return "total_invite_sent";
//     }
//   });
//   const activeItem = ApplicantStats.find((i) => i.id === activeTab);
//   const activeComponent = activeItem ? activeItem.component : <></>;

//   useEffect(() => {
//     const valid = ApplicantStats.some((s) => s.id === activeTab);
//     if (!valid && ApplicantStats[0]?.id) {
//       setActiveTab(ApplicantStats[0].id);
//     }
//   }, [data]);

//   // handle tab click
//   const handleTabClick = (d: { id: string }) => {
//     setActiveTab(d.id);
//     try {
//       localStorage.setItem(ACTIVE_TAB_KEY, d.id);
//     } catch (err) {
//       console.error("Failed to save active tab to localStorage:", err);
//     }
//   };

//   return (
//     <div className="page-wrapper">
//       <div className="content">
//         {getTotalCounts.loading ? (
//           <PageLoader />
//         ) : (
//           <>
//             {/* Tabs */}
//             <TabItems
//               tabList={ApplicantStats}
//               activeTab={activeTab}
//               handleClick={handleTabClick}
//             />

//             {/* Guide Container */}
//             <>
//               <div className="text-end">
//                 <Button
//                   className="border-secodary"
//                   onClick={() => setShowGuide((prev) => !prev)}
//                 >
//                   <i className="ti ti-info-circle-filled fs-16"></i>
//                   <div className="pb-1 ">Guide</div>
//                 </Button>
//               </div>

//               <GuideContentArea
//                 maxHeight={"400px"}
//                 showGuide={showGuide}
//                 handleClick={() => setShowGuide(false)}
//                 title={"Applicant Invited"}
//                 content={
//                   <div>
//                     <div className="pb-2">
//                       The applicant will receive the user credentials to access
//                       the Aupertz applicant portal through an invite.
//                     </div>
//                     <div className="pb-4">
//                       The applicant will log in to fill the applicant form (ADF)
//                       and submit all the required data for screening.
//                     </div>
//                     {guideData?.map((d) => (
//                       <div className="mb-3" style={{ maxWidth: "900px" }}>
//                         <h6 className="fw-semibold pb-1">{d.title}</h6>
//                         {d?.steps?.map((i, idx) => (
//                           <div className="d-flex gap-2 py-1">
//                             <div>{idx + 1}.</div>
//                             <div>{i}</div>
//                           </div>
//                         ))}
//                       </div>
//                     ))}
//                   </div>
//                 }
//               />
//             </>

//             {/* Tab Content */}
//             <div className="mt-2">{activeComponent}</div>

//             {/* Timeline Modal */}
//             {showTimeline && (
//               <TimelineModal
//                 timelineData={timelineData_invite}
//                 onClose={() => setShowTimeline(false)}
//               />
//             )}
//             {showModifyInvite && (
//               <ModifyInviteModal
//                 handleClose={() => setShowModifyInvite(false)}
//                 handleSubmit={(d) => {
//                   console.log(d);
//                   setShowModifyInvite(false);
//                 }}
//               />
//             )}
//           </>
//         )}
//       </div>

//       {/* ---- Footer ---- */}
//       <Footer />
//     </div>
//   );
// };
// export default ApplicantCaseTracking;
